import {Component, ContentChild, Input, OnInit, TemplateRef} from '@angular/core';
import {
    CustomerContact,
    CustomerSourcingService,
    InsuranceCategoryFamily,
    InsuranceService,
    InsuranceSubscription,
    InsuranceSubscriptionSearch,
    InsuranceSubscriptionService,
    SearchResult,
    SimplePaginationUtils,
} from '@lifeislife/lifeislife-domain';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {CustomerContextService} from '../../service/customer-context-service';
import {debounceTime, distinctUntilChanged, publishReplay, refCount, switchMap, tap} from 'rxjs/operators';
import {FamilyProductSubtitleDirective} from './family-product-subtitle.directive';
import {FamilyProductContentDirective} from './family-product-content.directive';
import {isSameRef} from '../../service/utils';
import {CustomerInsuranceInformationRequestHelperService} from '../../service/customer-insurance-information-request-helper.service';
import {FamilyProductSubheaderDirective} from './family-product-subheader-.directive';

@Component({
    selector: 'cf-family-product-list',
    templateUrl: './family-product-list.component.html',
    styleUrls: ['./family-product-list.component.scss'],
})
export class FamilyProductListComponent implements OnInit {

    @Input()
    family: InsuranceCategoryFamily;
    @Input()
    showFamilyRequestLink = false;
    @Input()
    showFamilyDetailsLink = true;
    @Input()
    alwaysShowFamilyDetailsLink = false;
    @Input()
    showEntryDetails = false;
    @Input()
    showEntryReserve = false;
    @Input()
    showMandateRequestButton = true;
    @Input()
    showLeftSubtitle = false;

    familyLabel: string;
    insuranceResults$: Observable<SearchResult<InsuranceSubscription>>;
    pagination = SimplePaginationUtils.newPagination(5);

    sendingInProgress$ = new BehaviorSubject<boolean>(false);
    sourcingRequestSent: boolean;
    sourcingRequestErrored: boolean;

    loading$ = new BehaviorSubject<boolean>(false);

    @ContentChild(FamilyProductSubtitleDirective, {static: false, read: TemplateRef})
    subtitleTemplateRef: TemplateRef<any>;
    @ContentChild(FamilyProductContentDirective, {static: false, read: TemplateRef})
    contentTemplateRef: TemplateRef<any>;
    @ContentChild(FamilyProductSubheaderDirective, {static: false, read: TemplateRef})
    subheaderTemplateRef: TemplateRef<any>;

    constructor(
        private sourcingService: CustomerSourcingService,
        private insuranceService: InsuranceService,
        private insuranceSubscriptionService: InsuranceSubscriptionService,
        private customerInsuranceInformationRequestHelperService: CustomerInsuranceInformationRequestHelperService,
        private customerContextService: CustomerContextService,
    ) {
    }

    ngOnInit(): void {
        this.familyLabel = this.insuranceService.getInsuranceFamilyLabel(this.family);

        this.insuranceResults$ = this.searchSubscription$();
    }

    private searchSubscription$() {
        return this.customerContextService.activeCustomerContact$.pipe(
            distinctUntilChanged((ca, cb) => isSameRef(ca, cb)),
            debounceTime(0),
            tap(() => this.loading$.next(true)),
            switchMap(c => this.searchCustomerContactsubscriptions$(c)),
            tap(() => this.loading$.next(false)),
            publishReplay(1), refCount(),
        );
    }

    private searchCustomerContactsubscriptions$(customerContact: CustomerContact): Observable<SearchResult<InsuranceSubscription>> {
        if (customerContact == null) {
            return of(new SearchResult<InsuranceSubscription>());
        }

        const insuranceSubscriptionSearch: InsuranceSubscriptionSearch = {
            insuranceSearch: {
                categorySearch: {
                    anyFamily: [this.family],
                },
            },
            insuranceSubscriptionStatusSearch: {
                allVisibleToCustomers: true,
            },
            subscriptionSearch: {
                archived: false,
                customerSearch: {
                    exactCustomerRef: customerContact.customerRef,
                },
            },
        };

        return this.insuranceSubscriptionService.searchInsuranceSubscriptions$(insuranceSubscriptionSearch, this.pagination);
    }


    onNewSourcingRequestClick(family: InsuranceCategoryFamily) {
        this.customerInsuranceInformationRequestHelperService.createDraft$(family)
            .subscribe();
    }


    onMoreClick() {
        const customerRef = this.customerContextService.getActiveCustomerRef();
        const contactRef = this.customerContextService.getActiveContactRef();

        this.sourcingRequestErrored = false;
        this.sourcingRequestSent = false;

        this.sendingInProgress$.next(true);
        this.sourcingService.notifyPotentialCustomerFromCustomerFront$({
            customerRef: customerRef,
            contactRef: contactRef,
            family: this.family,
        }).subscribe(
            () => this.onSourcingRequetsSent(),
            e => this.onSourcingRequestError(e),
        );
    }

    private onSourcingRequetsSent() {
        this.sendingInProgress$.next(false);
        this.sourcingRequestSent = true;
    }

    private onSourcingRequestError(error: any) {
        this.sendingInProgress$.next(false);
        this.sourcingRequestSent = false;
        this.sourcingRequestErrored = true;
        console.warn(error);
    }

}
